import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Monitor, Moon, Sun } from "lucide-react";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useVisibleNav } from "@/lib/rbac/use-nav";
import { useTheme, type Theme } from "@/lib/theme";

const THEME_ACTIONS: { value: Theme; label: string; icon: typeof Sun }[] = [
  { value: "light", label: "Light theme", icon: Sun },
  { value: "dark", label: "Dark theme", icon: Moon },
  { value: "system", label: "System theme", icon: Monitor },
];

/**
 * ⌘K / Ctrl+K palette: jump to any page the user can see, or switch theme.
 * Built from the same RBAC-filtered nav as the sidebar.
 */
export function CommandPalette({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const navigate = useNavigate();
  const sections = useVisibleNav();
  const { theme, setTheme } = useTheme();

  // Global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === "k" && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open, onOpenChange]);

  const run = (action: () => void) => {
    onOpenChange(false);
    action();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="top-[12vh] translate-y-0 data-[state=closed]:slide-out-to-top-[10vh] data-[state=open]:slide-in-from-top-[10vh] max-w-xl gap-0 overflow-hidden p-0 [&>button:last-child]:hidden">
        <DialogTitle className="sr-only">Go to page</DialogTitle>
        <DialogDescription className="sr-only">
          Search pages and actions. Use the arrow keys to move and Enter to open.
        </DialogDescription>
        <Command className="[&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:text-[11px] [&_[cmdk-group-heading]]:font-semibold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-input]]:h-12">
          <CommandInput placeholder="Search pages, e.g. “grocery orders” or “onboarding”…" />
          <CommandList className="max-h-[60vh]">
            <CommandEmpty>No pages found.</CommandEmpty>

            {sections.flatMap((section) =>
              section.groups.map((group) => (
                <CommandGroup
                  key={group.id}
                  heading={
                    group.isSingle || !section.label
                      ? section.label ?? "General"
                      : `${section.label} · ${group.label}`
                  }
                >
                  {group.items.map((item) => {
                    const label = group.isSingle ? group.label : item.label;
                    return (
                      <CommandItem
                        key={item.id}
                        // cmdk matches on value + keywords; include the
                        // group / subgroup so "grocery orders" finds it.
                        value={`${item.id} ${label}`}
                        keywords={[group.label, section.label ?? "", item.subgroup ?? ""]}
                        onSelect={() => run(() => navigate(item.path))}
                        className="gap-2.5"
                      >
                        <item.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="truncate">{label}</span>
                        {!group.isSingle && (
                          <span className="ml-auto truncate pl-3 text-xs text-muted-foreground">
                            {item.subgroup ? `${group.label} · ${item.subgroup}` : group.label}
                          </span>
                        )}
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
              ))
            )}

            <CommandSeparator />
            <CommandGroup heading="Preferences">
              {THEME_ACTIONS.map((action) => (
                <CommandItem
                  key={action.value}
                  value={`theme ${action.label}`}
                  keywords={["appearance", "mode", "dark", "light"]}
                  onSelect={() => run(() => setTheme(action.value))}
                  className="gap-2.5"
                >
                  <action.icon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span>{action.label}</span>
                  {theme === action.value && (
                    <span className="ml-auto text-xs text-muted-foreground">Current</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
          <div className="flex items-center gap-4 border-t px-3 py-2 text-[11px] text-muted-foreground">
            <span>
              <Kbd>↑</Kbd> <Kbd>↓</Kbd> to navigate
            </span>
            <span>
              <Kbd>Enter</Kbd> to open
            </span>
            <span>
              <Kbd>Esc</Kbd> to close
            </span>
          </div>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

function Kbd({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="rounded border bg-muted px-1 py-px font-sans text-[10px] font-medium">
      {children}
    </kbd>
  );
}
